import { type FormEvent, useEffect } from 'react'
import { ASSET_ICON } from '../constants'
import type { AuthProviders, AuthPurpose, AuthTab, Candidato, ModalStep } from '../types'
import { ProcessSteps } from './ProcessSteps'

interface AuthModalProps {
  open: boolean
  step: ModalStep
  authTab: AuthTab
  authPurpose: AuthPurpose
  providers: AuthProviders
  candidato: Candidato | null
  tituloVaga: string
  msg: string
  erro: string
  enviando: boolean
  processoPassos: string[]
  onClose: () => void
  onSetAuthTab: (tab: AuthTab) => void
  onOAuth: (provider: 'google' | 'linkedin') => void
  onAuthSubmit: (data: { nome: string; email: string; senha: string }) => void
  onCandSubmit: (fd: FormData) => void
  onIrMinhas: () => void
}

export function AuthModal({
  open,
  step,
  authTab,
  authPurpose,
  providers,
  candidato,
  tituloVaga,
  msg,
  erro,
  enviando,
  processoPassos,
  onClose,
  onSetAuthTab,
  onOAuth,
  onAuthSubmit,
  onCandSubmit,
  onIrMinhas,
}: AuthModalProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const isAuth = step === 'auth'
  const isSucesso = step === 'sucesso'
  const isCriar = authTab === 'criar'

  let hdTitle = `Candidatar-se — ${tituloVaga}`
  if (isAuth) {
    hdTitle =
      authPurpose === 'minhas' ? 'Entre para ver suas vagas' : 'Entre para candidatar-se'
  }
  if (isSucesso) hdTitle = 'Candidatura enviada'

  const handleAuth = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    onAuthSubmit({
      nome: String(fd.get('nome') || '').trim(),
      email: String(fd.get('email') || '').trim().toLowerCase(),
      senha: String(fd.get('senha') || ''),
    })
  }

  const handleCand = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onCandSubmit(new FormData(e.currentTarget))
  }

  return (
    <div
      className="cr-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="cr-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cr-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cr-modal-hd">
          <h3 id="cr-modal-title">{hdTitle}</h3>
          <button type="button" className="cr-x" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>
        <div className="cr-modal-bd">
          {isSucesso ? (
            <>
              <div className="cr-success-banner">
                <span className="cr-emblem-plate cr-emblem-plate--md">
                  <img src={ASSET_ICON} width={56} height={56} alt="" />
                </span>
                <h3>Recebemos sua candidatura</h3>
                <p>{msg || 'Obrigado por se candidatar ao Grupo HI.'}</p>
                <p>Acompanhe o que acontece a seguir:</p>
              </div>
              <ProcessSteps doneCount={1} passos={processoPassos} celebrate />
            </>
          ) : (
            <>
              {msg && <div className="cr-alert ok">{msg}</div>}
              {erro && <div className="cr-alert err">{erro}</div>}
              {isAuth ? (
                <>
                  <div className="cr-auth-tabs" role="tablist">
                    <button
                      type="button"
                      className={`cr-auth-tab${!isCriar ? ' on' : ''}`}
                      onClick={() => onSetAuthTab('entrar')}
                    >
                      Entrar
                    </button>
                    <button
                      type="button"
                      className={`cr-auth-tab${isCriar ? ' on' : ''}`}
                      onClick={() => onSetAuthTab('criar')}
                    >
                      Criar conta
                    </button>
                  </div>
                  {(providers.google || providers.linkedin) && (
                    <>
                      <div className="cr-oauth-list">
                        {providers.google && (
                          <button
                            type="button"
                            className="cr-btn cr-oauth google"
                            onClick={() => onOAuth('google')}
                          >
                            Continuar com Google
                          </button>
                        )}
                        {providers.linkedin && (
                          <button
                            type="button"
                            className="cr-btn cr-oauth linkedin"
                            onClick={() => onOAuth('linkedin')}
                          >
                            Continuar com LinkedIn
                          </button>
                        )}
                      </div>
                      <div className="cr-divider">
                        <span>ou use e-mail</span>
                      </div>
                    </>
                  )}
                  <form id="cr-form-auth" onSubmit={handleAuth}>
                    {isCriar && (
                      <div>
                        <label htmlFor="cr-auth-nome">Nome completo *</label>
                        <input id="cr-auth-nome" name="nome" required autoComplete="name" />
                      </div>
                    )}
                    <div>
                      <label htmlFor="cr-auth-email">E-mail *</label>
                      <input
                        id="cr-auth-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                      />
                    </div>
                    <div>
                      <label htmlFor="cr-auth-senha">Senha *</label>
                      <input
                        id="cr-auth-senha"
                        name="senha"
                        type="password"
                        required
                        minLength={8}
                        autoComplete={isCriar ? 'new-password' : 'current-password'}
                        placeholder={isCriar ? 'Mínimo 8 caracteres' : ''}
                      />
                    </div>
                  </form>
                </>
              ) : (
                <form
                  className="cr-modal-bd"
                  id="cr-form-cand"
                  onSubmit={handleCand}
                  style={{ padding: 0, gap: 12, display: 'flex', flexDirection: 'column' }}
                >
                  <p className="cr-muted" style={{ margin: 0 }}>
                    Enviando como <strong>{candidato?.nome || ''}</strong> ({candidato?.email || ''})
                  </p>
                  <input type="hidden" name="nome" value={candidato?.nome || ''} />
                  <input type="hidden" name="email" value={candidato?.email || ''} />
                  <div>
                    <label htmlFor="cr-tel">Telefone</label>
                    <input
                      id="cr-tel"
                      name="telefone"
                      type="tel"
                      autoComplete="tel"
                      placeholder="(83) 99999-0000"
                      defaultValue={candidato?.telefone || ''}
                    />
                  </div>
                  <div>
                    <label htmlFor="cr-cv">Currículo (PDF, DOC ou DOCX)</label>
                    <input
                      id="cr-cv"
                      name="curriculo"
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf"
                    />
                  </div>
                  <div>
                    <label htmlFor="cr-msg">Mensagem (opcional)</label>
                    <textarea id="cr-msg" name="mensagem" placeholder="Conte um pouco sobre você…" />
                  </div>
                </form>
              )}
            </>
          )}
        </div>
        <div className="cr-modal-ft">
          {isSucesso ? (
            <>
              <button type="button" className="cr-btn cr-btn-ghost" onClick={onClose}>
                Entendi
              </button>
              <button type="button" className="cr-btn cr-btn-primary" onClick={onIrMinhas}>
                Ver minhas vagas
              </button>
            </>
          ) : (
            <>
              <button type="button" className="cr-btn cr-btn-ghost" onClick={onClose}>
                Cancelar
              </button>
              {isAuth ? (
                <button
                  type="submit"
                  form="cr-form-auth"
                  className="cr-btn cr-btn-primary"
                  disabled={enviando}
                >
                  {enviando ? 'Aguarde…' : isCriar ? 'Criar conta' : 'Entrar'}
                </button>
              ) : (
                <button
                  type="submit"
                  form="cr-form-cand"
                  className="cr-btn cr-btn-primary"
                  disabled={enviando}
                >
                  {enviando ? 'Enviando…' : 'Enviar candidatura'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
